import {
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import { Image, Spin, Tooltip } from 'antd';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  AttachmentIconButton,
  AttachmentViewerBackdrop,
  AttachmentViewerWrapper,
  StyledIconClose,
  StyledIconDownload,
  StyledIconNext,
  StyledIconPrevious,
  StyledIconPrint,
} from './styles';
import type { AttachmentViewerProps } from './types';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const PDF_RASTER_BASELINE_SHARPNESS = 1.42;
const PDF_RASTER_ZOOM_QUALITY_SLOPE = 0.4;
const PDF_RASTER_QUALITY_MAX_BOOST = 2.85;
const CONTAINER_WIDTH_QUANTIZE_PX = 4;
const CONTAINER_WIDTH_UPDATE_THRESHOLD_PX = 8;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;
const TOOLBAR_ICON_STYLE = {
  fontSize: 18,
  color: 'var(--color-attachment-viewer-toolbar-fg, #ffffff)',
} as const;

function quantizeContainerWidth(width: number): number {
  return Math.max(
    0,
    Math.round(width / CONTAINER_WIDTH_QUANTIZE_PX) * CONTAINER_WIDTH_QUANTIZE_PX,
  );
}

function extensionOf(fileName?: string): string {
  return fileName?.split('.').pop()?.toLowerCase() ?? '';
}

function mimeTypeForExtension(ext: string): string {
  switch (ext) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    case 'pdf':
      return 'application/pdf';
    default:
      return '';
  }
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

export const AttachmentViewer = ({
  onClose,
  transactionReference = '',
  attachments = [],
  attachmentLoading,
  providerLogo,
  drawerOffset,
  zIndex,
  embedded,
  standaloneWidth,
  standaloneFullHeight,
  centered,
  topSlot,
  toolbarStart,
  emptyActions,
  menuWidth = 0,
}: AttachmentViewerProps) => {
  const { t } = useTranslation();
  const expandedEmbeddedLayout = Boolean(embedded && standaloneFullHeight);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [imageRotation, setImageRotation] = useState(0);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [scaledImageSize, setScaledImageSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const previewAreaRef = useRef<HTMLDivElement>(null);
  const viewerWrapperRef = useRef<HTMLDivElement>(null);
  const measureContainerRafRef = useRef<number | null>(null);

  useEffect(() => {
    if (currentIndex >= attachments.length) {
      setCurrentIndex(0);
    }
  }, [attachments.length, attachments, currentIndex]);

  const attachmentOpenContextKey = `${transactionReference}|${
    attachments[currentIndex]?.attachmentID ?? ''
  }|${currentIndex}`;

  useEffect(() => {
    setImageRotation(0);
    setPreviewZoom(1);
    setNumPages(null);
  }, [previewUrl, attachmentOpenContextKey]);

  const measureContainer = useCallback(() => {
    const el = previewAreaRef.current;
    if (!el) return;
    const style = window.getComputedStyle(el);
    const paddingX =
      parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const nextWidth = quantizeContainerWidth(
      Math.max(0, el.clientWidth - paddingX),
    );
    setContainerWidth((prev) => {
      if (prev === nextWidth) return prev;
      if (prev === 0) return nextWidth;
      if (Math.abs(prev - nextWidth) < CONTAINER_WIDTH_UPDATE_THRESHOLD_PX) {
        return prev;
      }
      return nextWidth;
    });
  }, []);

  const scheduleMeasureContainer = useCallback(() => {
    if (measureContainerRafRef.current != null) return;
    measureContainerRafRef.current = requestAnimationFrame(() => {
      measureContainerRafRef.current = null;
      measureContainer();
    });
  }, [measureContainer]);

  useLayoutEffect(() => {
    scheduleMeasureContainer();
  }, [
    standaloneWidth,
    drawerOffset,
    embedded,
    standaloneFullHeight,
    centered,
    scheduleMeasureContainer,
  ]);

  useEffect(() => {
    if (!centered) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [centered, onClose]);

  useEffect(() => {
    scheduleMeasureContainer();
    window.addEventListener('resize', scheduleMeasureContainer);
    return () => {
      window.removeEventListener('resize', scheduleMeasureContainer);
      if (measureContainerRafRef.current != null) {
        cancelAnimationFrame(measureContainerRafRef.current);
        measureContainerRafRef.current = null;
      }
    };
  }, [scheduleMeasureContainer]);

  useEffect(() => {
    const el = previewAreaRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(() => {
      scheduleMeasureContainer();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [scheduleMeasureContainer, attachmentLoading, attachments.length]);

  const currentAttachmentForPreview = attachments[currentIndex];
  const currentAttachmentPreviewKey = `${
    currentAttachmentForPreview?.attachmentID ?? ''
  }|${currentIndex}|${currentAttachmentForPreview?.fileName ?? ''}`;

  useEffect(() => {
    if (!currentAttachmentForPreview?.fileContent) {
      setPreviewUrl(null);
      return undefined;
    }

    const ext = extensionOf(currentAttachmentForPreview.fileName);
    const mimeType = mimeTypeForExtension(ext);
    const uint8Array = base64ToUint8Array(currentAttachmentForPreview.fileContent);
    const blobBuffer = uint8Array.buffer.slice(
      uint8Array.byteOffset,
      uint8Array.byteOffset + uint8Array.byteLength,
    ) as ArrayBuffer;
    const blob = mimeType
      ? new Blob([blobBuffer], { type: mimeType })
      : new Blob([blobBuffer]);
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [currentAttachmentPreviewKey, currentAttachmentForPreview?.fileContent]);

  useEffect(() => {
    if (!imageDimensions) return;
    const layoutWidth =
      containerWidth > 0
        ? containerWidth
        : previewAreaRef.current?.getBoundingClientRect().width ?? 0;
    if (layoutWidth <= 0) return;

    let width: number;
    let height: number;
    if (imageRotation === 90 || imageRotation === 270) {
      height = layoutWidth;
      width = height * (imageDimensions.width / imageDimensions.height);
    } else {
      width = layoutWidth;
      height = width / (imageDimensions.width / imageDimensions.height);
    }
    setScaledImageSize({ width, height });
  }, [imageRotation, imageDimensions, containerWidth, previewZoom]);

  useEffect(() => {
    setImageDimensions(null);
    setScaledImageSize(null);
  }, [currentIndex]);

  const overlayZIndex = centered ? zIndex ?? 1300 : zIndex;

  const wrapViewer = (node: ReactElement) => {
    if (!centered || typeof document === 'undefined') {
      return node;
    }
    return createPortal(
      <>
        <AttachmentViewerBackdrop
          $zIndex={overlayZIndex}
          onClick={onClose}
          aria-hidden
        />
        {node}
      </>,
      document.body,
    );
  };

  const wrapperProps = {
    ref: viewerWrapperRef,
    $menuWidth: menuWidth,
    $drawerOffset: drawerOffset,
    $zIndex: overlayZIndex,
    $embedded: embedded,
    $standaloneWidth: standaloneWidth,
    $standaloneFullHeight: standaloneFullHeight,
    $centered: centered,
    role: centered ? ('dialog' as const) : undefined,
    'aria-modal': centered ? true : undefined,
  };

  if (attachmentLoading) {
    return wrapViewer(
      <AttachmentViewerWrapper
        {...wrapperProps}
        aria-label={
          centered
            ? transactionReference || t('vat-report.previewing-attachment')
            : undefined
        }
        style={{ alignItems: 'stretch', justifyContent: 'flex-start' }}
      >
        <div className="av-top-bar">
          <div className="av-top-bar-left av-file-info">{providerLogo}</div>
          <div className="av-top-bar-actions">
            {topSlot ?? (
              <div className="av-previewing-text">
                {t('vat-report.previewing-attachment')}
              </div>
            )}
            <AttachmentIconButton
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
            >
              <StyledIconClose />
            </AttachmentIconButton>
          </div>
        </div>
        <div
          style={{
            flex: 1,
            minHeight: 200,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spin size="large" />
        </div>
      </AttachmentViewerWrapper>,
    );
  }

  if (!attachments.length) {
    return wrapViewer(
      <AttachmentViewerWrapper
        {...wrapperProps}
        aria-label={
          centered
            ? transactionReference || t('vat-report.previewing-attachment')
            : undefined
        }
      >
        <div style={{ position: 'absolute', top: 20, right: 24 }}>
          <AttachmentIconButton
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <StyledIconClose />
          </AttachmentIconButton>
        </div>
        <div className="av-centered-content">
          <div className="av-transaction-reference">{transactionReference}</div>
          <div className="av-no-attachment-text">
            {t('vat-report.you-have-no-linked-attachments')}
          </div>
          {emptyActions}
        </div>
      </AttachmentViewerWrapper>,
    );
  }

  const currentAttachment = attachments[currentIndex];
  const fileExtension = extensionOf(currentAttachment?.fileName);
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension);
  const isPdf = fileExtension === 'pdf';
  const fileName =
    currentAttachment?.fileName || t('vat-report.no-file-name');

  const pdfBaseWidth = containerWidth > 0 ? containerWidth : 320;
  const pdfRenderWidth = pdfBaseWidth * previewZoom;
  const rasterQualitySupportsCssZoom =
    typeof window !== 'undefined' &&
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    CSS.supports('zoom', '1');
  const zoomQualityFactor =
    1 + Math.max(0, previewZoom - 1) * PDF_RASTER_ZOOM_QUALITY_SLOPE;
  const rasterQualityBoost = rasterQualitySupportsCssZoom
    ? Math.min(
        PDF_RASTER_QUALITY_MAX_BOOST,
        PDF_RASTER_BASELINE_SHARPNESS * zoomQualityFactor,
      )
    : 1;
  const pdfRasterWidth = Math.ceil(pdfRenderWidth * rasterQualityBoost);
  const pdfRasterZoomWrapStyle: CSSProperties | undefined =
    rasterQualityBoost > 1
      ? ({ zoom: 1 / rasterQualityBoost } as CSSProperties)
      : undefined;

  return wrapViewer(
    <AttachmentViewerWrapper
      {...wrapperProps}
      aria-label={
        centered
          ? currentAttachment?.fileName ||
            transactionReference ||
            t('vat-report.previewing-attachment')
          : undefined
      }
      style={{ alignItems: 'stretch', justifyContent: 'flex-start' }}
    >
      <div className="av-top-bar">
        <div className="av-top-bar-left av-file-info">
          {providerLogo}
          <Tooltip title={fileName}>
            <span className="av-file-name">{fileName}</span>
          </Tooltip>
        </div>
        <div className="av-top-bar-actions">
          {topSlot ?? (
            <div className="av-previewing-text">
              {t('vat-report.previewing-x-of-y', {
                current: currentIndex + 1,
                total: attachments.length,
              })}
            </div>
          )}
          {attachments.length > 1 ? (
            <>
              <Tooltip title={t('vat-report.previous-attachment')}>
                <span>
                  <AttachmentIconButton
                    type="button"
                    onClick={() =>
                      setCurrentIndex((prev) =>
                        prev > 0 ? prev - 1 : attachments.length - 1,
                      )
                    }
                    aria-label={t('vat-report.previous-attachment')}
                  >
                    <StyledIconPrevious />
                  </AttachmentIconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('vat-report.next-attachment')}>
                <span>
                  <AttachmentIconButton
                    type="button"
                    onClick={() =>
                      setCurrentIndex((prev) =>
                        prev < attachments.length - 1 ? prev + 1 : 0,
                      )
                    }
                    aria-label={t('vat-report.next-attachment')}
                  >
                    <StyledIconNext />
                  </AttachmentIconButton>
                </span>
              </Tooltip>
            </>
          ) : null}
          <AttachmentIconButton
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <StyledIconClose />
          </AttachmentIconButton>
        </div>
      </div>
      <div className="av-toolbar">
        {toolbarStart}
        <div className="av-toolbar-icon-group">
          {(isImage || isPdf) && (
            <>
              <Tooltip title={t('vat-report.zoom-out')}>
                <span
                  className="av-toolbar-icon"
                  onClick={() =>
                    setPreviewZoom((zoom) => Math.max(ZOOM_MIN, zoom - ZOOM_STEP))
                  }
                  style={{
                    opacity: previewZoom <= ZOOM_MIN ? 0.5 : 1,
                    cursor: previewZoom <= ZOOM_MIN ? 'not-allowed' : 'pointer',
                  }}
                >
                  <ZoomOutOutlined style={TOOLBAR_ICON_STYLE} />
                </span>
              </Tooltip>
              <Tooltip title={t('vat-report.zoom-in')}>
                <span
                  className="av-toolbar-icon"
                  onClick={() =>
                    setPreviewZoom((zoom) => Math.min(ZOOM_MAX, zoom + ZOOM_STEP))
                  }
                  style={{
                    opacity: previewZoom >= ZOOM_MAX ? 0.5 : 1,
                    cursor: previewZoom >= ZOOM_MAX ? 'not-allowed' : 'pointer',
                  }}
                >
                  <ZoomInOutlined style={TOOLBAR_ICON_STYLE} />
                </span>
              </Tooltip>
            </>
          )}
          {isImage && (
            <>
              <Tooltip title={t('vat-report.rotate-clockwise')}>
                <span
                  className="av-toolbar-icon"
                  onClick={() => setImageRotation((prev) => (prev + 90) % 360)}
                >
                  <RotateRightOutlined style={TOOLBAR_ICON_STYLE} />
                </span>
              </Tooltip>
              <Tooltip title={t('vat-report.rotate-counter-clockwise')}>
                <span
                  className="av-toolbar-icon"
                  onClick={() =>
                    setImageRotation((prev) => (prev - 90 + 360) % 360)
                  }
                >
                  <RotateLeftOutlined style={TOOLBAR_ICON_STYLE} />
                </span>
              </Tooltip>
            </>
          )}
        </div>
        <div className="av-toolbar-icon-group">
          <Tooltip title={t('vat-report.download-file')}>
            <span
              className="av-toolbar-icon"
              onClick={() => {
                if (!previewUrl || !currentAttachment?.fileName) return;
                const link = document.createElement('a');
                link.href = previewUrl;
                link.setAttribute('download', currentAttachment.fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              <StyledIconDownload className="av-toolbar-svg" />
            </span>
          </Tooltip>
          <Tooltip title={t('vat-report.print-file')}>
            <span
              className="av-toolbar-icon"
              onClick={() => {
                if (!previewUrl) {
                  window.alert(t('vat-report.could-not-get-document-for-printing'));
                  return;
                }
                const printWindow = window.open(previewUrl, '_blank');
                if (!printWindow) {
                  window.alert(t('vat-report.could-not-open-print-window'));
                  return;
                }
                printWindow.onload = () => {
                  setTimeout(() => {
                    printWindow.focus();
                    printWindow.print();
                  }, 500);
                };
              }}
            >
              <StyledIconPrint className="av-toolbar-svg" />
            </span>
          </Tooltip>
        </div>
      </div>
      <div className="av-preview-area">
        <div ref={previewAreaRef} className="av-pdf-container">
          {isImage && previewUrl ? (
            <div
              style={{
                display: 'block',
                width: '100%',
                ...(expandedEmbeddedLayout
                  ? { height: 'auto', maxHeight: '100%' }
                  : { height: '100%' }),
                overflow: 'auto',
              }}
            >
              <div
                style={{
                  width: scaledImageSize
                    ? imageRotation === 90 || imageRotation === 270
                      ? scaledImageSize.height * previewZoom
                      : scaledImageSize.width * previewZoom
                    : '100%',
                  height: scaledImageSize
                    ? imageRotation === 90 || imageRotation === 270
                      ? scaledImageSize.width * previewZoom
                      : scaledImageSize.height * previewZoom
                    : expandedEmbeddedLayout
                      ? 'auto'
                      : '100%',
                  transform:
                    (imageRotation === 90
                      ? 'rotate(90deg) translateY(-100%)'
                      : imageRotation === 180
                        ? 'rotate(180deg) translate(-100%,-100%)'
                        : imageRotation === 270
                          ? 'rotate(270deg) translateX(-100%)'
                          : 'rotate(0deg)') + ` scale(${previewZoom})`,
                  transformOrigin: 'top left',
                  transition: 'transform 0.2s',
                  display: 'block',
                }}
              >
                <div
                  style={{
                    transformOrigin: 'top left',
                    width:
                      imageRotation === 90 || imageRotation === 270
                        ? scaledImageSize?.height
                        : scaledImageSize?.width,
                    height:
                      imageRotation === 90 || imageRotation === 270
                        ? scaledImageSize?.width
                        : scaledImageSize?.height,
                  }}
                >
                  <Image
                    key={`preview-${containerWidth}-${previewZoom}-${imageRotation}`}
                    src={previewUrl}
                    alt={
                      currentAttachment.fileName ||
                      t('vat-report.attachment-preview')
                    }
                    style={
                      imageRotation === 90 || imageRotation === 270
                        ? {
                            height: scaledImageSize?.height,
                            width: 'auto',
                            maxHeight: '100%',
                            display: 'block',
                          }
                        : {
                            width: scaledImageSize?.width,
                            height: 'auto',
                            maxWidth: '100%',
                            display: 'block',
                          }
                    }
                    preview={false}
                    onLoad={(event) => {
                      const img = event.target as HTMLImageElement;
                      setImageDimensions({
                        width: img.naturalWidth,
                        height: img.naturalHeight,
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          ) : isPdf && previewUrl ? (
            <div
              className="av-pdf-zoom-container"
              style={{
                width: '100%',
                ...(expandedEmbeddedLayout
                  ? { minHeight: 0, overflow: 'visible' }
                  : { height: '100%', overflow: 'auto' }),
              }}
            >
              <div
                style={{
                  width: pdfRenderWidth,
                  minHeight: expandedEmbeddedLayout ? 'auto' : '100%',
                }}
              >
                <div
                  className="av-pdf-raster-zoom-wrap"
                  style={pdfRasterZoomWrapStyle}
                >
                  <div
                    style={{
                      width: pdfRasterWidth,
                      minHeight: expandedEmbeddedLayout ? 'auto' : '100%',
                    }}
                  >
                    <Document
                      key={previewUrl}
                      file={previewUrl}
                      onLoadSuccess={({ numPages: nextPages }) =>
                        setNumPages(nextPages)
                      }
                    >
                      {Array.from(new Array(numPages || 0), (_, index) => (
                        <Page
                          key={`page_${index + 1}`}
                          pageNumber={index + 1}
                          width={pdfRasterWidth}
                        />
                      ))}
                    </Document>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                textAlign: 'center',
                color: 'var(--color-text-muted, #666)',
                fontSize: 14,
              }}
            >
              <p>{t('vat-report.preview-not-available-for-file-type')}</p>
              <p style={{ marginTop: 8 }}>
                <a
                  href={previewUrl || '#'}
                  download={currentAttachment.fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => {
                    if (!previewUrl) event.preventDefault();
                  }}
                >
                  {t('vat-report.download-to-view')}
                </a>
              </p>
            </div>
          )}
        </div>
      </div>
    </AttachmentViewerWrapper>,
  );
};

export default AttachmentViewer;
